import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { cx, css } from "@/styled-system/css";
import { Box, IconButton } from "@zillow/constellation-10";
import { IconChevronUpFilled, IconChevronDownFilled, IconChevronDownOutline } from "@zillow/constellation-icons";
import React, { useEffect, useState, useLayoutEffect } from "react";
import { useDropdownBehavior } from "../../hooks/useDropdownBehavior.esm.js";
import { GROUP_WIDTH_MAP } from "../../mixins/components/GroupMixin.esm.js";
import { parseSubnavItems } from "../../utils/parseSubnavItems.esm.js";
import { FONT_FAMILY_UI } from "../_constellationUiTokens.esm.js";
import SearchBox from "./SearchBox.esm.js";
import SubnavDropdown from "./SubnavDropdown.esm.js";
const DESKTOP_NAV_MIN_WIDTH_PX = 1024;
const SUBNAV_UI_FONT_STYLE = {
  fontFamily: FONT_FAMILY_UI
};
const b2bSubnavRootShell = css({
  width: "100%",
  /** Lets the strip shrink inside flex parents instead of forcing document horizontal scroll. */
  minWidth: 0,
  fontFamily: FONT_FAMILY_UI,
  fontSmoothing: "antialiased",
  backgroundColor: "bg.neutral",
  borderBottomWidth: "{borderWidths.25}",
  borderBottomStyle: "solid",
  borderBottomColor: "border.muted",
  "& a, & button": {
    fontFamily: FONT_FAMILY_UI
  }
});
const b2bSubnavRootOpenMobile = css({
  position: "relative",
  zIndex: "zIndex.popover"
});
const subnavLinkNonInteractive = css({
  cursor: "default"
});
const subnavLinkBoldLabel = css({
  fontFamily: FONT_FAMILY_UI,
  fontSize: "body-sm",
  lineHeight: "body-sm",
  fontWeight: "bold"
});
const subnavLinkBlogHome = css({
  fontFamily: FONT_FAMILY_UI,
  textDecoration: "none",
  color: "text.neutral",
  fontSmoothing: "antialiased",
  display: "inline-flex",
  alignItems: "center",
  /** Horizontal padding from the `xl` breakpoint; uses semantic `default` spacing. */
  xl: {
    paddingTop: "tighter",
    paddingBottom: "tighter",
    paddingLeft: "default",
    paddingRight: "default"
  },
  /** Narrow viewports: horizontal inset comes from the subnav row; vertical padding only on the link. */
  xlDown: {
    paddingTop: "tighter",
    paddingBottom: "tighter",
    paddingLeft: 0,
    paddingRight: 0
  },
  _hover: {
    color: "text.action.hero.default",
    textDecoration: "underline",
    textDecorationColor: "border.muted",
    textDecorationThickness: "{borderWidths.default}",
    textUnderlineOffset: "spacing.micro.default"
  }
});
const subnavLinkBlogFlat = css({
  fontFamily: FONT_FAMILY_UI,
  fontSize: "body-sm",
  lineHeight: "body-xs",
  fontWeight: "normal",
  textDecoration: "none",
  color: "text.subtle",
  fontSmoothing: "antialiased",
  display: "flex",
  alignItems: "center",
  xl: {
    paddingTop: "tighter",
    paddingBottom: "tighter",
    paddingLeft: "default",
    paddingRight: "default"
  },
  _hover: {
    color: "text.action.hero.default",
    textDecoration: "underline",
    textDecorationColor: "border.muted",
    textDecorationThickness: "{borderWidths.default}",
    textUnderlineOffset: "spacing.micro.default"
  }
});
const subnavLinkDropdown = css({
  fontFamily: FONT_FAMILY_UI,
  fontSize: "body-sm",
  lineHeight: "body-sm",
  fontWeight: "normal",
  textDecoration: "none",
  color: "text.action.hero.default",
  fontSmoothing: "antialiased",
  display: "block",
  width: "100%",
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  boxSizing: "border-box",
  _hover: {
    textDecoration: "underline",
    textDecorationColor: "border.muted",
    textDecorationThickness: "{borderWidths.default}",
    textUnderlineOffset: "spacing.micro.default",
    color: "text.action.hero.hover"
  }
});
function subnavLinkClassName(home, inDropdown) {
  if (inDropdown) {
    return subnavLinkDropdown;
  }
  if (home) {
    return subnavLinkBlogHome;
  }
  return subnavLinkBlogFlat;
}
function subnavLinkUsesBoldInnerLabel(home, inDropdown) {
  if (inDropdown) return false;
  return Boolean(home);
}
const decodeHtmlEntities = (text) => {
  const entityMap = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#039;": "'",
    "&apos;": "'",
    "&ndash;": "–",
    "&mdash;": "—",
    "&nbsp;": " "
  };
  return text.replace(/&(?:amp|lt|gt|quot|#039|apos|ndash|mdash|nbsp);/g, (match) => entityMap[match] || match);
};
const SUBNAV_HEIGHT_PX = 58;
const subnavRowLayoutStyle = {
  width: "100%",
  maxWidth: GROUP_WIDTH_MAP.xl,
  marginLeft: "auto",
  marginRight: "auto",
  boxSizing: "border-box"
};
const subnavMobileMenuIconButton = css({
  xl: {
    display: "none"
  },
  xlDown: {
    flexShrink: 0,
    alignSelf: "center",
    marginTop: 0,
    backgroundColor: "transparent",
    _hover: {
      backgroundColor: "transparent"
    },
    _active: {
      backgroundColor: "blue.200"
    },
    _focusVisible: {
      backgroundColor: "transparent",
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineOffset: "2px",
      outlineColor: "blue.500"
    }
  }
});
const subnavRowSearchOuter = css({
  flexShrink: 0,
  minWidth: 0,
  marginLeft: "auto"
});
const subnavRowSearchOuterExpanded = css({
  flex: "1 1 0%",
  width: "100%",
  maxWidth: "100%",
  marginLeft: "0"
});
const useBelowDesktopNav = () => {
  const [match, setMatch] = useState(false);
  useLayoutEffect(() => {
    const mq = window.matchMedia(`(max-width: ${DESKTOP_NAV_MIN_WIDTH_PX - 1}px)`);
    const update = () => setMatch(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return match;
};
const SubnavLink = ({
  url,
  title,
  home,
  inDropdown = false,
  styles
}) => {
  const linkClass = subnavLinkClassName(home, inDropdown);
  const boldInner = subnavLinkUsesBoldInnerLabel(home, inDropdown);
  if (!url || url.trim() === "") {
    return /* @__PURE__ */ jsx(Box, { className: cx(linkClass, subnavLinkNonInteractive), css: styles, style: SUBNAV_UI_FONT_STYLE, ...boldInner ? {} : {
      dangerouslySetInnerHTML: {
        __html: title
      }
    }, children: boldInner ? /* @__PURE__ */ jsx("span", { className: subnavLinkBoldLabel, dangerouslySetInnerHTML: {
      __html: title
    } }) : null });
  }
  return boldInner ? /* @__PURE__ */ jsx("a", { href: url, className: cx(linkClass, styles ? css(styles) : void 0), style: SUBNAV_UI_FONT_STYLE, children: /* @__PURE__ */ jsx("span", { className: subnavLinkBoldLabel, dangerouslySetInnerHTML: {
    __html: title
  } }) }) : /* @__PURE__ */ jsx("a", { href: url, className: cx(linkClass, styles ? css(styles) : void 0), style: SUBNAV_UI_FONT_STYLE, dangerouslySetInnerHTML: {
    __html: title
  } });
};
const SubnavItemWithDropdown = ({
  item
}) => {
  const {
    dropdown,
    ref,
    onMouseEnter,
    onMouseLeave
  } = useDropdownBehavior();
  const hasChildren = item.children && item.children.length > 0;
  if (hasChildren) {
    return /* @__PURE__ */ jsxs(Box, { ref, css: {
      position: "relative"
    }, onMouseEnter, onMouseLeave, children: [
      /* @__PURE__ */ jsxs(Box, { css: {
        display: "flex",
        alignItems: "center",
        columnGap: 0
      }, children: [
        /* @__PURE__ */ jsx(SubnavLink, { url: item.url, title: item.title, styles: {
          xl: {
            paddingTop: "tighter",
            paddingBottom: "tighter",
            paddingLeft: "default",
            paddingRight: "default"
          }
        } }),
        /* @__PURE__ */ jsx(IconButton, { emphasis: "bare", size: "xs", type: "button", title: dropdown ? `Close ${decodeHtmlEntities(item.title)} menu` : `Open ${decodeHtmlEntities(item.title)} menu`, "aria-haspopup": "menu", "aria-expanded": dropdown ? "true" : "false", "aria-label": dropdown ? `Close ${decodeHtmlEntities(item.title)} menu` : `Open ${decodeHtmlEntities(item.title)} menu`, icon: /* @__PURE__ */ jsx(IconChevronDownOutline, {}), css: {
          minWidth: "auto",
          width: "auto",
          paddingInlineStart: 0,
          paddingInlineEnd: 0,
          marginLeft: "calc({spacing.200} * -1)",
          _hover: {
            backgroundColor: "transparent"
          }
        } })
      ] }),
      /* @__PURE__ */ jsx(SubnavDropdown, { isOpen: dropdown, renderLink: (childItem) => /* @__PURE__ */ jsx(SubnavLink, { url: childItem.url, title: childItem.title, inDropdown: true }), children: item.children || [] })
    ] });
  }
  return /* @__PURE__ */ jsx(SubnavLink, { url: item.url, title: item.title });
};
const MobileSubnavItem = ({
  item,
  isNested = false
}) => {
  var _a;
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const fontSize = "body";
  if (hasChildren) {
    return /* @__PURE__ */ jsxs(Box, { css: {
      ...!isNested && {
        borderBottomWidth: "{borderWidths.default}",
        borderBottomStyle: "solid",
        borderBottomColor: "border.muted"
      },
      width: "100%"
    }, children: [
      /* @__PURE__ */ jsxs(Box, { css: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        ...isNested ? {
          paddingBlock: "tighter",
          paddingInline: "tight"
        } : {
          padding: "default"
        }
      }, children: [
        /* @__PURE__ */ jsx(Box, { asChild: true, css: {
          display: "flex",
          alignItems: "center",
          flex: 1,
          fontFamily: FONT_FAMILY_UI,
          textDecoration: "none",
          fontWeight: "normal",
          fontSize,
          lineHeight: "body",
          color: isNested ? "text.action.hero.default" : "text.subtle",
          fontSmoothing: "antialiased",
          _hover: {
            color: "text.action.hero.default"
          },
          _focus: {
            color: "text.action.hero.default"
          },
          _focusVisible: {
            color: "text.action.hero.default"
          }
        }, children: /* @__PURE__ */ jsx("a", { href: item.url, "aria-haspopup": "menu", "aria-expanded": isOpen ? "true" : "false", style: SUBNAV_UI_FONT_STYLE, dangerouslySetInnerHTML: {
          __html: item.title
        } }) }),
        /* @__PURE__ */ jsx(IconButton, { emphasis: "bare", size: "xs", title: isOpen ? `Close ${decodeHtmlEntities(item.title)} menu` : `Open ${decodeHtmlEntities(item.title)} menu`, "aria-haspopup": "menu", "aria-expanded": isOpen ? "true" : "false", "aria-label": isOpen ? `Close ${decodeHtmlEntities(item.title)} menu` : `Open ${decodeHtmlEntities(item.title)} menu`, icon: /* @__PURE__ */ jsx(IconChevronDownOutline, {}), onClick: () => setIsOpen(!isOpen), css: {
          _hover: {
            backgroundColor: "transparent"
          }
        } })
      ] }),
      isOpen && /* @__PURE__ */ jsx(Box, { asChild: true, css: {
        backgroundColor: "bg.neutral",
        borderTopWidth: "{borderWidths.default}",
        borderTopStyle: "solid",
        borderTopColor: "border.muted",
        paddingTop: "tightest",
        paddingBottom: "tightest",
        position: "relative",
        width: "100%",
        listStyle: "none",
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        paddingInlineStart: 0
      }, children: /* @__PURE__ */ jsx("ul", { children: (_a = item.children) == null ? void 0 : _a.map((child) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(MobileSubnavItem, { item: child, isNested: true }) }, child.id)) }) })
    ] });
  }
  return /* @__PURE__ */ jsx(Box, { css: {
    ...!isNested && {
      borderBottomWidth: "{borderWidths.default}",
      borderBottomStyle: "solid",
      borderBottomColor: "border.muted"
    },
    width: "100%"
  }, children: /* @__PURE__ */ jsx(Box, { asChild: true, css: {
    display: "block",
    fontFamily: FONT_FAMILY_UI,
    lineHeight: "body",
    ...isNested ? {
      paddingBlock: "tighter",
      paddingInline: "tight"
    } : {
      padding: "default"
    },
    textDecoration: "none",
    fontWeight: "normal",
    fontSize,
    fontSmoothing: "antialiased",
    color: isNested ? "text.action.hero.default" : "text.subtle",
    _hover: {
      color: "text.action.hero.default",
      /** SkunkWorks `backgroundBlue` / `blue100` #EEF6FF */
      backgroundColor: "blue.100"
    },
    _focus: {
      color: "text.action.hero.default",
      backgroundColor: "blue.100"
    },
    _focusVisible: {
      color: "text.action.hero.default",
      backgroundColor: "blue.100"
    }
  }, children: /* @__PURE__ */ jsx("a", { href: item.url, style: SUBNAV_UI_FONT_STYLE, dangerouslySetInnerHTML: {
    __html: item.title
  } }) }) });
};
const updateTopOffset = ({
  subnavRef,
  setTopOffset
}) => {
  if (!subnavRef.current) return;
  const parent = subnavRef.current.offsetParent;
  if (parent) {
    const parentRect = parent.getBoundingClientRect();
    const subnavRect = subnavRef.current.getBoundingClientRect();
    setTopOffset(subnavRect.top - parentRect.top);
  } else {
    const rect = subnavRef.current.getBoundingClientRect();
    setTopOffset(rect.top);
  }
};
const B2BSubnav = ({
  menu,
  primaryItem: primaryItemProp,
  items: itemsProp = [],
  searchPrefix: searchPrefixProp,
  searchPlaceholder: searchPlaceholderProp,
  isOpen: controlledIsOpen,
  onOpenChange
}) => {
  var _a, _b, _c;
  const items = menu ? parseSubnavItems(menu.items) : itemsProp;
  const primaryItem = ((_a = menu == null ? void 0 : menu.meta) == null ? void 0 : _a.title) && ((_b = menu == null ? void 0 : menu.meta) == null ? void 0 : _b.url) ? {
    id: 0,
    title: menu.meta.title,
    url: menu.meta.url,
    parent: 0
  } : primaryItemProp;
  const searchPrefix = ((_c = menu == null ? void 0 : menu.meta) == null ? void 0 : _c.search) || searchPrefixProp;
  const searchPlaceholder = searchPlaceholderProp;
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const [mobileSearchExpanded, setMobileSearchExpanded] = React.useState(false);
  const isControlled = controlledIsOpen !== void 0;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const [topOffset, setTopOffset] = React.useState(0);
  const subnavRef = React.useRef(null);
  const prevIsControlledRef = React.useRef(isControlled);
  const lastControlledValueRef = React.useRef(controlledIsOpen);
  const belowDesktopNav = useBelowDesktopNav();
  useEffect(() => {
    const wasControlled = prevIsControlledRef.current;
    const isNowControlled = isControlled;
    if (wasControlled && !isNowControlled) {
      setInternalIsOpen(lastControlledValueRef.current ?? false);
    }
    if (isControlled && controlledIsOpen !== void 0) {
      lastControlledValueRef.current = controlledIsOpen;
    }
    prevIsControlledRef.current = isControlled;
  }, [isControlled, controlledIsOpen]);
  useEffect(() => {
    if (isControlled && controlledIsOpen) {
      updateTopOffset({
        subnavRef,
        setTopOffset
      });
    }
  }, [controlledIsOpen, isControlled]);
  useEffect(() => {
    updateTopOffset({
      subnavRef,
      setTopOffset
    });
  }, []);
  useEffect(() => {
    if (isOpen) return;
    const updateOffset = () => {
      updateTopOffset({
        subnavRef,
        setTopOffset
      });
    };
    window.addEventListener("resize", updateOffset);
    window.addEventListener("scroll", updateOffset);
    return () => {
      window.removeEventListener("resize", updateOffset);
      window.removeEventListener("scroll", updateOffset);
    };
  }, [isOpen]);
  const handleToggle = () => {
    const newIsOpen = !isOpen;
    if (newIsOpen) {
      updateTopOffset({
        subnavRef,
        setTopOffset
      });
    }
    if (isControlled) {
      onOpenChange == null ? void 0 : onOpenChange(newIsOpen);
    } else {
      setInternalIsOpen(newIsOpen);
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  if (!items.length) {
    return null;
  }
  const mobileOverlayStyle = isOpen && belowDesktopNav ? {
    position: "absolute",
    top: `${topOffset}px`,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: `calc(100dvh - ${topOffset}px)`,
    minHeight: `calc(100dvh - ${topOffset}px)`
  } : void 0;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    isOpen && belowDesktopNav && /* @__PURE__ */ jsx("div", { "aria-hidden": true, style: {
      height: `${SUBNAV_HEIGHT_PX}px`,
      flexShrink: 0
    } }),
    /* @__PURE__ */ jsx(Box, { ref: subnavRef, "data-testid": "b2b-subnav", className: cx(b2bSubnavRootShell, isOpen && belowDesktopNav ? b2bSubnavRootOpenMobile : void 0), style: mobileOverlayStyle, children: /* @__PURE__ */ jsxs(Box, { css: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      width: "100%",
      xlDown: {
        backgroundColor: "bg.neutral",
        ...isOpen && {
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: "looser"
        }
      }
    }, children: [
      /* @__PURE__ */ jsxs(Box, { style: subnavRowLayoutStyle, css: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        height: `${SUBNAV_HEIGHT_PX}px`,
        minHeight: `${SUBNAV_HEIGHT_PX}px`,
        position: "relative",
        minWidth: 0,
        /** Narrow viewports: horizontal padding on the subnav strip. */
        xlDown: {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: "default",
          paddingRight: "default",
          ...isOpen && {
            flexShrink: 0,
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "bg.neutral"
          }
        }
      }, children: [
        /* @__PURE__ */ jsxs(Box, { css: {
          display: "flex",
          alignItems: "center",
          /** Nav cluster is content-sized and may shrink; search stays end-aligned via sibling margin. */
          flex: "0 1 auto",
          minWidth: 0,
          columnGap: 0,
          xlDown: {
            ...mobileSearchExpanded ? {
              display: "none"
            } : {}
          }
        }, children: [
          primaryItem && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(Box, { css: {
              display: "flex",
              alignItems: "center",
              /** Narrow viewports: primary link and menu control in one row group. */
              xlDown: {
                columnGap: "tightest",
                flexWrap: "nowrap"
              }
            }, children: [
              /* @__PURE__ */ jsx(SubnavLink, { url: primaryItem.url, title: primaryItem.title, home: true }),
              /* @__PURE__ */ jsx(IconButton, { type: "button", emphasis: "bare", size: "sm", "aria-expanded": isOpen ? "true" : "false", icon: isOpen ? /* @__PURE__ */ jsx(IconChevronUpFilled, {}) : /* @__PURE__ */ jsx(IconChevronDownFilled, {}), title: "Open menu", onClick: handleToggle, className: subnavMobileMenuIconButton, css: {
                justifyContent: "flex-start"
              } })
            ] }),
            /* @__PURE__ */ jsx(Box, { "aria-hidden": true, css: {
              display: "none",
              /** Vertical rule between primary link and desktop items (`xl` only). */
              xl: {
                display: "block",
                flexShrink: 0,
                alignSelf: "center",
                width: "{borderWidths.25}",
                height: "{spacing.800}",
                backgroundColor: "border.muted",
                marginLeft: "default",
                marginRight: "default"
              }
            } })
          ] }),
          !primaryItem && /* @__PURE__ */ jsx(IconButton, { type: "button", emphasis: "bare", size: "sm", "aria-expanded": isOpen ? "true" : "false", icon: isOpen ? /* @__PURE__ */ jsx(IconChevronUpFilled, {}) : /* @__PURE__ */ jsx(IconChevronDownFilled, {}), title: "Open menu", onClick: handleToggle, className: subnavMobileMenuIconButton, css: {
            justifyContent: "flex-start"
          } }),
          /* @__PURE__ */ jsx(Box, { css: {
            display: "none",
            xl: {
              display: "flex",
              alignItems: "stretch",
              columnGap: 0
            }
          }, children: items.map((item) => /* @__PURE__ */ jsx(SubnavItemWithDropdown, { item }, item.id)) })
        ] }),
        searchPrefix && /* @__PURE__ */ jsx(Box, { className: cx(subnavRowSearchOuter, mobileSearchExpanded ? subnavRowSearchOuterExpanded : void 0), children: /* @__PURE__ */ jsx(SearchBox, { placeholder: searchPlaceholder ? searchPlaceholder : "Search", actionPrefix: searchPrefix, onMobileSearchOpenChange: setMobileSearchExpanded }) })
      ] }),
      /* @__PURE__ */ jsx(Box, { css: {
        display: isOpen ? "block" : "none",
        listStyle: "none",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingInlineStart: 0,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        width: "100%",
        ...isOpen && {
          backgroundColor: "bg.neutral"
        },
        xl: {
          display: "none"
        },
        ...isOpen && {
          xlDown: {
            position: "relative",
            isolation: "isolate",
            zIndex: 1
          }
        }
      }, children: /* @__PURE__ */ jsx("ul", { style: {
        listStyle: "none",
        padding: 0,
        margin: 0
      }, children: items.map((item) => /* @__PURE__ */ jsx(Box, { asChild: true, children: /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(MobileSubnavItem, { item }) }) }, item.id)) }) })
    ] }) })
  ] });
};
export {
  B2BSubnav as default
};
